/* Import History Page (Iter 95 #3)
   Son 30 gun icinde commit edilmis tum import batchlerini listeler.
   Her satirda: tarih, dosya, eklenen sayisi + "Geri Al" butonu.
   Undo: o batch ile DB'ye yazilmis kayitlari (faturalar / banka tx) siler,
         eslesmeleri temizler.
*/
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Undo2, Loader2, History, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const KIND_LABEL = {
    invoices_xlsx: 'Fatura Excel',
    bank_xlsx: 'Banka Excel',
    bank_csv: 'Banka CSV',
};

export default function ImportHistoryPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [undoBusy, setUndoBusy] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/accounting/imports/history`, { headers: hdrs() })
            .then((r) => setItems(r.data.items || []))
            .catch(() => toast.error('Gecmis yuklenemedi'))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const undo = async (batch) => {
        const stats = batch.stats || {};
        const cnt = stats.committed_imported ?? (batch.imported_ids || []).length ?? 0;
        if (!window.confirm(
            `"${batch.filename}" dosyasi ile eklenen ${cnt} kayit silinecek.\n\n` +
            `Bagli olduklari banka eslesmeleri de cozulecek.\n\n` +
            `Devam edilsin mi?`,
        )) return;
        setUndoBusy(batch.id);
        try {
            const r = await axios.post(
                `${API}/api/accounting/imports/staging/${batch.id}/undo`,
                {}, { headers: hdrs() },
            );
            const d = r.data;
            toast.success(
                `${d.deleted_invoices || 0} fatura · ${d.deleted_tx || 0} banka tx silindi · ${d.cleaned_matches || 0} eslesme temizlendi`,
                { duration: 8000 },
            );
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Undo basarisiz');
        } finally {
            setUndoBusy(null);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="import-history-page">
            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center gap-3">
                    <button onClick={() => navigate('/accounting')}
                        className="p-2 hover:bg-gray-100 rounded-lg" data-testid="ih-back">
                        <ArrowLeft size={18} />
                    </button>
                    <div className="flex-1">
                        <div className="text-xs text-gray-500">Yonetim</div>
                        <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <History size={18} /> Import Gecmisi (Son 30 Gun)
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1200px] mx-auto px-6 py-4">
                {loading ? (
                    <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Yukleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-400">
                        Henuz commit edilmis import yok
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="grid grid-cols-[160px_120px_1fr_120px_120px_140px] gap-3 px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b">
                            <div>Tarih</div>
                            <div>Tip</div>
                            <div>Dosya</div>
                            <div className="text-right">Eklenen</div>
                            <div>Durum</div>
                            <div></div>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {items.map((b) => {
                                const stats = b.stats || {};
                                const cnt = stats.committed_imported ?? (b.imported_ids || []).length ?? 0;
                                const isUndone = b.status === 'undone';
                                const undo_stats = b.undo_stats || {};
                                return (
                                    <div key={b.id}
                                        className={`grid grid-cols-[160px_120px_1fr_120px_120px_140px] gap-3 px-4 py-2.5 items-center text-sm ${isUndone ? 'opacity-60' : ''}`}
                                        data-testid={`ih-row-${b.id}`}>
                                        <div className="text-xs text-gray-700">
                                            {(b.committed_at || b.created_at || '').slice(0, 16).replace('T', ' ')}
                                        </div>
                                        <div>
                                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-semibold">
                                                {KIND_LABEL[b.kind] || b.kind}
                                            </span>
                                        </div>
                                        <div className="text-gray-900 truncate" title={b.filename}>{b.filename}</div>
                                        <div className="text-right font-bold text-gray-900">{cnt}</div>
                                        <div>
                                            {isUndone ? (
                                                <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-700 rounded font-semibold">
                                                    Geri alindi
                                                </span>
                                            ) : (
                                                <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold flex items-center gap-1 w-fit">
                                                    <CheckCircle2 size={11} /> Aktif
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-end">
                                            {isUndone ? (
                                                <span className="text-[11px] text-gray-500">
                                                    -{undo_stats.deleted_invoices || 0} fatura · -{undo_stats.deleted_tx || 0} tx
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => undo(b)}
                                                    disabled={undoBusy === b.id || cnt === 0}
                                                    className="h-7 px-2.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                                                    data-testid={`ih-undo-${b.id}`}>
                                                    {undoBusy === b.id ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={11} />}
                                                    Geri Al
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
